import { useState } from 'react';
import { endsAfterStart } from '@erp/rpc';
import { overrideWindowIsComplete } from './OverrideWindowFields';
import {
  dayBounds,
  exclusiveEndIsoToDateInput,
  exclusiveEndIsoToMonthInput,
  isoToLocalDateInput,
  isoToUtcMonthInput,
  localDateInMonths,
  monthBounds,
  toReviewAtIso,
  utcMonthInMonths,
  type OverrideWindowGrain,
} from './overrideWindow';

// The window every procurement override form edits: when it starts and stops
// applying, when it comes up for review, and why it was set. The forms differ
// in the values they override and in their grain (calendar days for lead-time
// and inventory-policy overrides, whole UTC months for growth factors), so the
// window state, its default horizon, and the conversions in both directions
// live here rather than being kept in step by hand.
// See docs/procurement/lead-time-overrides.md, decision 2.

/** Default horizon: the buyer can clear it to make the override open-ended. */
const DEFAULT_HORIZON_MONTHS = 6;

// Day grain: today through today + 6 months. Month grain: this month through
// the fifth after it, so the same six forecast buckets are covered.
function defaultEndsAt(grain: OverrideWindowGrain): string {
  return grain === 'month'
    ? utcMonthInMonths(DEFAULT_HORIZON_MONTHS - 1)
    : localDateInMonths(DEFAULT_HORIZON_MONTHS);
}

/** The window of an existing override, as both row DTOs express it. */
interface OverrideWindowRow {
  /** Optional so a DTO that does not surface it can still be adopted. */
  startsAt?: string | null;
  endsAt: string | null;
  reviewAt: string | null;
  reason: string | null;
}

/** The window as both override APIs take it. */
export interface OverrideWindowPayload {
  /** Omitted rather than null when blank: the server defaults it to now. */
  startsAt: string | undefined;
  endsAt: string | null;
  reviewAt: string | null;
  reason: string | undefined;
}

export interface OverrideWindowState {
  grain: OverrideWindowGrain;
  /**
   * Local YYYY-MM-DD (day grain) or UTC YYYY-MM (month grain), or '' to start
   * now. Every override kind gates on `starts_at <= now` server-side, so a
   * future date schedules the row: it is saved and listed as "scheduled" but
   * changes nothing until it arrives. The exception is a growth factor, which
   * reaches its own forecast buckets ahead of time by design.
   */
  startsAt: string;
  setStartsAt: (value: string) => void;
  /** Same shape as `startsAt`; '' for open-ended. Month grain: the LAST month covered. */
  endsAt: string;
  setEndsAt: (value: string) => void;
  /** Local YYYY-MM-DD, or '' for no review date: a review is a day at either grain. */
  reviewAt: string;
  setReviewAt: (value: string) => void;
  reason: string;
  setReason: (value: string) => void;
  /**
   * An override must end or come up for review, and every bound entered must
   * parse; false blocks the save.
   */
  isComplete: boolean;
  /**
   * The end must fall after the start (or after now, when the start is blank):
   * the contract's rule, judged here so the form can say so before the server
   * does. True when there is no end.
   */
  orderValid: boolean;
  /** Back to the default horizon, for a fresh override. */
  reset: () => void;
  /** Take on the window of the row being edited. */
  adopt: (o: OverrideWindowRow) => void;
  payload: OverrideWindowPayload;
}

/** An existing row's window as the form's input strings. */
function windowInputs(
  grain: OverrideWindowGrain,
  o: OverrideWindowRow,
): { startsAt: string; endsAt: string; reviewAt: string; reason: string } {
  const month = grain === 'month';
  return {
    startsAt: o.startsAt
      ? month
        ? isoToUtcMonthInput(o.startsAt)
        : isoToLocalDateInput(o.startsAt)
      : '',
    endsAt: o.endsAt
      ? month
        ? exclusiveEndIsoToMonthInput(o.endsAt)
        : exclusiveEndIsoToDateInput(o.endsAt)
      : '',
    reviewAt: o.reviewAt ? isoToLocalDateInput(o.reviewAt) : '',
    reason: o.reason ?? '',
  };
}

/**
 * `initial` seeds the state from an existing row on first render — for a form
 * that mounts already editing, where `adopt` would need an effect.
 */
export function useOverrideWindowState(
  grain: OverrideWindowGrain = 'day',
  initial?: OverrideWindowRow,
): OverrideWindowState {
  const [seed] = useState(() =>
    initial ? windowInputs(grain, initial) : null,
  );
  // Blank by default, so an override with no start date entered begins now,
  // exactly as before the field existed.
  const [startsAt, setStartsAt] = useState(seed?.startsAt ?? '');
  const [endsAt, setEndsAt] = useState(
    () => seed?.endsAt ?? defaultEndsAt(grain),
  );
  const [reviewAt, setReviewAt] = useState(seed?.reviewAt ?? '');
  const [reason, setReason] = useState(seed?.reason ?? '');

  function reset() {
    setStartsAt('');
    setEndsAt(defaultEndsAt(grain));
    setReviewAt('');
    setReason('');
  }

  function adopt(o: OverrideWindowRow) {
    const inputs = windowInputs(grain, o);
    setStartsAt(inputs.startsAt);
    setEndsAt(inputs.endsAt);
    setReviewAt(inputs.reviewAt);
    setReason(inputs.reason);
  }

  const bounds =
    grain === 'month'
      ? monthBounds(startsAt, endsAt)
      : dayBounds(startsAt, endsAt);
  const payload: OverrideWindowPayload = {
    startsAt: bounds.startsAt,
    endsAt: bounds.endsAt,
    reviewAt: reviewAt ? toReviewAtIso(reviewAt) : null,
    reason: reason.trim() || undefined,
  };

  return {
    grain,
    startsAt,
    setStartsAt,
    endsAt,
    setEndsAt,
    reviewAt,
    setReviewAt,
    reason,
    setReason,
    isComplete: !bounds.malformed && overrideWindowIsComplete(endsAt, reviewAt),
    orderValid: endsAfterStart(payload),
    reset,
    adopt,
    payload,
  };
}
